// @givmo/app-charity — payment-profile vocabulary (CPA-WEB4).
//
// The payment-profile view's translated, HONEST vocabulary: the display copy + Badge tones for a
// profile's `verification_state`, the payout-readiness signal, the method label, and the audit-history
// event copy. This is the §3.4 "surface status/flags honestly" layer — it must NEVER imply money will
// flow when the profile is flagged or in a state we don't recognize.
//
// Wire values (`CharityPaymentProfileOut.method` / `.verification_state`, `CharityPaymentEventOut
// .event_type`) are typed as plain STRINGS in the generated client (open enums), so we treat them as
// OPAQUE and switch on the known set with a tolerant fallback — an unknown future value renders as a
// neutral, honest label and is NEVER treated as payout-ready (fail-safe: unknown ⇒ not ready). This
// keeps a shipped bundle correct if the backend adds a state.
//
// ── The real lifecycle (backend, `origin/development`) — TWO facts, decided at different times ────
// 1. The NAME MATCH is SYNCHRONOUS: `CharityPaymentService.submit_ach` name-matches the account-holder
//    name against the charity's legal name in the request and stores the outcome on the new profile —
//    AUTO_ACCEPTED or FLAGGED_NAME_MISMATCH. A check election stores NOT_APPLICABLE (no name to match).
//    Nothing ever re-writes `verification_state`: the ONLY mutation to an existing profile is
//    `supersede()` flipping `status` ACTIVE→SUPERSEDED when a newer election replaces it
//    (`_persist_new_profile`). A bank change is therefore not a re-verification of the old profile —
//    it creates a NEW profile that lands already accepted or already flagged, and supersedes the old.
// 2. Whether grants can ACTUALLY be sent by bank transfer is a LATER, SEPARATE fact: after the submit
//    commits, the backend's payee auto-sync creates the Mercury recipient and writes the binding for
//    the profile — seconds later in the background, possibly delayed, possibly failed. The wire reports
//    it as `transfers_enabled`: always `false` on the submit responses (the sync is post-commit), and
//    `true` on the profile read once the binding exists. It is `false` for a check election and for an
//    unbound profile, and ABSENT on a backend that predates the member. A FLAGGED_NAME_MISMATCH
//    profile a reviewer APPROVES is bound the same way — and since nothing rewrites
//    `verification_state` or `flagged`, that read answers `flagged: true` AND `transfers_enabled: true`
//    (the common shape for a bank account held under a DBA name).
//
// The honesty rule follows from the split: "Verified" describes fact 1 and is claimed from the name
// match alone; money-will-flow copy, the success tone and `isPayoutReady` require fact 2 as well. An
// AUTO_ACCEPTED ACH profile without `transfers_enabled` is verified, neutral, and promises nothing.
//
// PRECEDENCE. On the ACH rail, `transfers_enabled === true` DOMINATES `flagged` and
// `verification_state`: an enabled profile renders exactly the enabled state — label "Verified", the
// success tone, the bank-transfer promise, payout-ready, no polling — whatever else the wire says,
// because the binding is the one fact that decides whether grants go out. Only when it is not true do
// the other rules apply, in this order: the organization changed hands after these details were
// entered (`awaiting_owner_bank_details`, CPA-BE69) → mailed checks until a current Owner enters the
// organization's bank details; flagged → under review; AUTO_ACCEPTED → verified and neutral;
// unrecognized → neutral and never ready. Every presentation reads `isUnderReview`, never
// `isFlaggedState` directly.

/** SaaS Badge tones (the subset the payment UI uses). */
export type PaymentBadgeTone = 'success' | 'info' | 'warning' | 'danger' | 'neutral' | 'brand';

/**
 * The facts the vocabulary reads off a profile. Structural, so the masked wire type
 * (`CharityPaymentProfileOut`), a submit result's profile and a test fixture all fit.
 *
 * `transfers_enabled` is OPTIONAL on purpose: absent reads as `false`. A backend that predates the
 * member, or a submit response (which always carries `false`), must land on the conservative copy —
 * never on a promise the wire did not make.
 */
export interface PaymentProfileFacts {
  verification_state: string;
  flagged: boolean;
  method: string;
  transfers_enabled?: boolean;
  /** CPA-BE69. Optional on purpose, like `transfers_enabled`: absent reads as `false`. */
  awaiting_owner_bank_details?: boolean;
}

/** Who reads the card, where its words depend on it (CPA-BE69's state after a change of ownership). */
export interface PaymentReader {
  /**
   * The organization's Global Admin (the `Owner` role row, `isOwnerRole`): after a takeover only a
   * current Global Admin's own entry re-binds the bank link (D-CP-101's floor), so only they are told
   * to enter the details. A Donations Owner by grant holds the payment write but is not one.
   */
  isGlobalAdmin: boolean;
}

// ── Payment methods (CharityPaymentProfileOut.method) ─────────────────────────────────────────────
// LOWERCASE on the wire — these are the enum VALUES, not the member names (`CharityPaymentMethod.ACH
// = "ach"`). `status` on the same DTO is uppercase (ACTIVE/SUPERSEDED); do not assume one casing.
export const PAYMENT_METHOD = {
  ach: 'ach',
  check: 'check',
} as const;

// ── Verification states (CharityPaymentProfileOut.verification_state) ─────────────────────────────
// The complete set. Every ACH election resolves to one of the first two in the submit request itself;
// every check election is the third.
export const VERIFICATION_STATE = {
  /** ACH: the account-holder name matched the charity's legal name. Clear to be paid. */
  autoAccepted: 'AUTO_ACCEPTED',
  /** ACH: the name did NOT match. Recorded, queued for human review, and never auto-bound. */
  flaggedNameMismatch: 'FLAGGED_NAME_MISMATCH',
  /** A check election — there is no ACH account-holder name to match, so nothing is verified. */
  notApplicable: 'NOT_APPLICABLE',
} as const;

// ── Audit history event kinds (CharityPaymentEventOut.event_type) ─────────────────────────────────
// The complete set. CREATED/CHANGED record the election itself (CHANGED for any subsequent election,
// including a switch to the check rail); VERIFIED/FLAGGED record the ACH name-match outcome beside it.
export const PAYMENT_EVENT_TYPE = {
  created: 'CREATED',
  changed: 'CHANGED',
  verified: 'VERIFIED',
  flagged: 'FLAGGED',
} as const;

/**
 * The ONLY payout-ready signal: is this election settled and clear to receive grant money?
 *
 * Ready = an ACH profile **with transfers enabled** (whatever `verification_state` and `flagged`
 * say — precedence, header comment), or NOT_APPLICABLE **on a check election** and not flagged.
 *
 * The name match alone is NOT readiness. It is decided in the submit request; whether grants can
 * actually be sent by bank transfer is decided later, when the backend's payee sync binds the profile
 * to a live recipient — and the wire reports THAT as `transfers_enabled` (header comment, fact 2).
 * Until it is `true`, the profile is verified but nothing may promise money will flow. Absent reads
 * as `false` (`isTransfersEnabled`), so an older backend lands on the conservative side. A flagged
 * profile a reviewer approved is bound with `flagged` still true: it is ready, not under review.
 *
 * NOT_APPLICABLE counts as ready deliberately. A check election has nothing to verify — the check
 * goes to the IRS address on file, there is no account-holder name to match, and no state it is
 * waiting to leave. Rendering it as unsettled would be dishonest in the opposite direction: it would
 * tell an org that finished setup that something is still outstanding. This is NOT the server-side
 * CPA-BE6 auto-bind floor, which classifies a check election as PENDING — that floor asks the
 * narrower question "may this ACH election be auto-bound to a Mercury recipient?", is explicitly
 * out-of-scope for the check rail, and never rides a charity-facing wire.
 *
 * That arm is gated on the METHOD because NOT_APPLICABLE means "there was no ACH name to match",
 * which is only true of a check election. The wire cannot pair it with `ach` today — `create_ach`
 * only ever emits AUTO_ACCEPTED or FLAGGED_NAME_MISMATCH — but the tolerant-reader posture exists
 * for the wire we have not seen yet, and an uninterpretable profile must never read as ready.
 *
 * Without the binding, FLAGGED_NAME_MISMATCH and any unknown future state are NOT ready (fail-safe
 * default). Callers use this to decide whether to say money will flow; they must never imply it
 * otherwise.
 */
export function isPayoutReady(profile: PaymentProfileFacts): boolean {
  if (isTransfersEnabled(profile)) return true;
  if (isFlaggedState(profile)) return false;
  // AUTO_ACCEPTED without the binding is verified, not ready: fact 2 is what makes the ACH rail ready.
  return (
    profile.verification_state === VERIFICATION_STATE.notApplicable && isCheckMethod(profile.method)
  );
}

/**
 * True only when the wire says grants can actually be sent to this ACH profile by bank transfer — the
 * backend's `transfers_enabled`, read strictly (`=== true`) on the ACH rail. A missing member (an
 * older backend, or a submit response, which never carries `true`), any non-boolean shape, and any
 * other method read as NOT enabled: the direction that promises nothing.
 *
 * This is the DOMINANT fact (header comment: precedence). It is true for a name-mismatch profile a
 * reviewer approved, whose `flagged` stays true on the wire — so every presentation checks it FIRST.
 */
export function isTransfersEnabled(profile: PaymentProfileFacts): boolean {
  return isAchMethod(profile.method) && profile.transfers_enabled === true;
}

/**
 * True for an ACH profile that passed the name match but whose transfers are not (yet) enabled:
 * AUTO_ACCEPTED, not flagged, `transfers_enabled` not `true`. This is the window between the submit
 * committing and the payee sync binding the profile — the state the Payments page polls in, and the
 * one presentation that is verified WITHOUT being payout-ready. A check election never enters it, and
 * a flagged profile is under review rather than waiting (a reviewer's approval lands it enabled
 * directly, hours or days later — not something a poll waits on).
 */
export function isAwaitingTransferSetup(profile: PaymentProfileFacts): boolean {
  return (
    isAchMethod(profile.method) &&
    !isFlaggedState(profile) &&
    profile.verification_state === VERIFICATION_STATE.autoAccepted &&
    !isTransfersEnabled(profile) &&
    // Nothing later binds these details on its own: only an Owner's new entry can (CPA-BE69).
    !isAwaitingOwnerBankDetails(profile)
  );
}

/**
 * True when the backend says these bank details wait for a current Owner's (CPA-BE69): the
 * organization changed hands after they were entered (a takeover; D-CP-95, D-CP-101), so they are
 * not used for bank transfers and grants are sent by mailed check until an Owner enters the
 * organization's bank details. Read strictly (`=== true`) on the ACH rail; absent (a backend before
 * the member) reads false.
 *
 * Enabled transfers dominate it (precedence, header comment), and it dominates review and the
 * verified state: a reviewer's approval no longer binds these details, and their name match was the
 * previous entry's.
 */
export function isAwaitingOwnerBankDetails(profile: PaymentProfileFacts): boolean {
  return (
    isAchMethod(profile.method) &&
    !isTransfersEnabled(profile) &&
    profile.awaiting_owner_bank_details === true
  );
}

/**
 * The RAW wire fact: the account-holder name mismatch flagged this profile for human review.
 *
 * The wire pairs the two fields — `flagged` is derived from the state (`_profile_out` sets it from
 * `CharityPaymentProfile.is_flagged()`, which is exactly `verification_state ==
 * FLAGGED_NAME_MISMATCH`) — so either alone is sufficient. Reading both keeps the honest direction
 * (under review) winning if they ever disagree.
 *
 * ⚠ Not a presentation. Approval never clears it: an approved mismatch is bound with `flagged` still
 * true, and that profile is enabled, not under review. Presentations read `isUnderReview`.
 */
export function isFlaggedState(profile: { verification_state: string; flagged: boolean }): boolean {
  return profile.flagged || profile.verification_state === VERIFICATION_STATE.flaggedNameMismatch;
}

/**
 * True when the name-mismatch review is what stands between this profile and grants: flagged on the
 * wire AND transfers not enabled. The one flagged presentation ("Under review", the danger tone, the
 * held-grants copy) keys on this — never on `isFlaggedState` alone, which stays true after a reviewer
 * approves the mismatch and the payee sync binds the profile (header comment: precedence).
 */
export function isUnderReview(profile: PaymentProfileFacts): boolean {
  return (
    !isTransfersEnabled(profile) && !isAwaitingOwnerBankDetails(profile) && isFlaggedState(profile)
  );
}

/**
 * The plain-language STATUS LABEL a charity admin sees on the masked profile. Derived from the
 * binding, the verification state and the flagged flag, NOT from a raw wire string — so we can never
 * accidentally show "Verified" while the profile is still under review. An enabled ACH profile reads
 * "Verified" whatever the wire's state says (header comment: precedence; an approved mismatch stays
 * flagged on the wire). Tolerant: an unknown state humanizes gracefully.
 *
 * A check election reads "Ready", never "Verified": nothing about it was verified. NOT_APPLICABLE on
 * any other method is an incoherent pairing, so it humanizes like an unrecognized state rather than
 * claiming a readiness the description beside it contradicts.
 */
export function verificationLabel(profile: PaymentProfileFacts): string {
  if (isTransfersEnabled(profile)) return 'Verified';
  if (isAwaitingOwnerBankDetails(profile)) return 'Action needed';
  if (isUnderReview(profile)) return 'Under review';
  switch (profile.verification_state) {
    case VERIFICATION_STATE.autoAccepted:
      return 'Verified';
    case VERIFICATION_STATE.notApplicable:
      if (isCheckMethod(profile.method)) return 'Ready';
      break;
    default:
      break;
  }
  // Tolerant reader: unknown or incoherent state → neutral humanized string (never payout-ready).
  return humanizeToken(profile.verification_state);
}

/**
 * Badge tone for the verification state. A PAYOUT-READY election reads success — checked first,
 * because an approved mismatch is flagged on the wire AND enabled, and it is green; under review
 * reads danger. A verified ACH profile whose transfers are not yet enabled reads neutral: its label
 * still says "Verified" (true), but the green tone is the money-will-flow claim and that is not true
 * yet.
 */
export function verificationTone(profile: PaymentProfileFacts): PaymentBadgeTone {
  if (isPayoutReady(profile)) return 'success';
  if (isAwaitingOwnerBankDetails(profile)) return 'warning';
  return isUnderReview(profile) ? 'danger' : 'neutral';
}

/**
 * A short, honest, plain-language explanation of what the current state means for the charity's
 * grants. NEVER says money will flow unless the profile is genuinely payout-ready. Copy is
 * donor/charity-honest.
 *
 * The bank-transfer promise is selected by `transfers_enabled` ALONE (header comment: precedence) —
 * an approved name-mismatch profile gets it while still flagged on the wire. The verified-but-not-
 * enabled profile states the verified fact and nothing more — no setup talk, no timing, no promise —
 * by ruling (2026-09-18): anything beyond it "just adds confusion".
 *
 * After a change of ownership (CPA-BE69) the copy says why (the organization was claimed again) and
 * that grants go by mailed check to the address on file with the IRS (the Charity Terms' Payments
 * section's own fact) until the organization's bank details are entered. The cause names no change
 * of admin and no previous owner: the same party's re-claim after a removal reaches this state too
 * (a processed removal keeps the bank link, D-CP-11), and the wire does not tell the two apart. Only
 * a current Global Admin's own entry re-binds the link (D-CP-101's floor; review-33's F2), so only
 * the Global Admin is told "you" and that the account on file, if it is still right, is entered again
 * (the same numbers re-bind the same payee). Anyone else, a Donations Owner by grant or a Member, is
 * told a Global Admin enters the details, and so is a reader the caller did not name.
 *
 * The flagged copy promises no notification: approving a payee flag sends the charity nothing and
 * never rewrites this profile's state. What it changes is the BINDING — the next read carries
 * `transfers_enabled: true` and this function selects the promise above. The other way out is
 * entering a bank account whose holder name matches.
 */
export function verificationDescription(
  profile: PaymentProfileFacts,
  reader?: PaymentReader,
): string {
  if (isTransfersEnabled(profile)) {
    return 'Your bank account is verified and bank transfers are enabled. Grants will be paid to you by bank transfer.';
  }
  if (isAwaitingOwnerBankDetails(profile)) {
    if (reader?.isGlobalAdmin === true) {
      return "Because your organization was claimed again, grants are sent by mailed check to the address on file with the IRS until you enter your organization's bank details. If the account below is still correct, enter it again.";
    }
    return "Because your organization was claimed again, grants are sent by mailed check to the address on file with the IRS until a Global Admin enters your organization's bank details.";
  }
  if (isUnderReview(profile)) {
    return "The account-holder name didn't match your organization's legal name, so our team is confirming it before enabling transfers. No grants are sent until that's resolved — or you can enter a bank account whose holder name matches your legal name.";
  }
  switch (profile.verification_state) {
    case VERIFICATION_STATE.autoAccepted:
      return 'Your bank account details are verified.';
    case VERIFICATION_STATE.notApplicable:
      // NOT_APPLICABLE only ever pairs with the check rail on the wire; if it somehow arrives on an
      // ACH profile the pairing is incoherent, so fall through to the neutral copy below.
      if (isCheckMethod(profile.method)) {
        return "You're set up to receive grants by mailed check, sent to your organization's IRS address on file.";
      }
      break;
    default:
      break;
  }
  return 'Your payment details are saved. No grants go out until this method is confirmed.';
}

/** True when the active election is the mailed-check rail. */
export function isCheckMethod(method: string): boolean {
  return method === PAYMENT_METHOD.check;
}

/** True when the active election is the ACH (bank-transfer) rail. */
export function isAchMethod(method: string): boolean {
  return method === PAYMENT_METHOD.ach;
}

/** Plain-language method label (tolerant of unknown values). */
export function methodLabel(method: string): string {
  switch (method) {
    case PAYMENT_METHOD.ach:
      return 'Bank transfer (ACH)';
    case PAYMENT_METHOD.check:
      return 'Mailed check';
    default:
      return humanizeToken(method);
  }
}

// ── Audit history event vocabulary (CharityPaymentEventOut) ───────────────────────────────────────

/** Humanized event kind. CHANGED covers any re-election, not only a bank swap. Tolerant of unknowns. */
export function paymentEventLabel(eventType: string): string {
  switch (eventType) {
    case PAYMENT_EVENT_TYPE.created:
      return 'Payment details added';
    case PAYMENT_EVENT_TYPE.changed:
      return 'Payment method changed';
    case PAYMENT_EVENT_TYPE.verified:
      return 'Verified';
    case PAYMENT_EVENT_TYPE.flagged:
      return 'Flagged for review';
    default:
      return humanizeToken(eventType);
  }
}

/**
 * Badge tone for a history event kind. A re-election reads warning because it redirects where money
 * goes — the same change the backend alerts every admin about.
 */
export function paymentEventTone(eventType: string): PaymentBadgeTone {
  switch (eventType) {
    case PAYMENT_EVENT_TYPE.verified:
      return 'success';
    case PAYMENT_EVENT_TYPE.flagged:
      return 'danger';
    case PAYMENT_EVENT_TYPE.changed:
      return 'warning';
    default:
      return 'neutral';
  }
}

/**
 * Translate an event's `detail` note into charity-facing copy, or null to render nothing.
 *
 * `detail` is an INTERNAL token, not copy: `method:ach`, `name_auto_accepted`, `name_mismatch_near`,
 * and for an operator import a provenance-stamped `method:ach;source:…;mercury_recipient_id:…`. We
 * translate the tokens we know and return null for everything else, so an internal note can never
 * reach a charity admin verbatim.
 */
export function paymentEventDetail(detail: string | null | undefined): string | null {
  if (!detail) return null;
  const head = detail.split(';')[0] ?? '';
  if (head.startsWith('method:')) {
    const method = head.slice('method:'.length);
    return method ? methodLabel(method) : null;
  }
  switch (detail) {
    case 'name_auto_accepted':
      return 'Account-holder name matched your legal name';
    case 'name_mismatch':
    case 'name_mismatch_near':
      return "Account-holder name didn't match your legal name";
    default:
      return null;
  }
}

/** Turn a SCREAMING_SNAKE_CASE wire token into a human-readable label ("SOME_STATE" → "Some state"). */
export function humanizeToken(token: string): string {
  const cleaned = token.replace(/[_-]+/g, ' ').trim().toLowerCase();
  if (cleaned.length === 0) return token;
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

/**
 * Format the masked account tail for display. The profile carries ONLY `account_last4` (never the full
 * number — by construction, rail §3.4). This renders the canonical masked form `•••• 1234`. Returns null
 * when there's no last-4 (e.g. a mailed-check election has no bank account).
 */
export function maskedAccount(last4: string | null | undefined): string | null {
  if (!last4) return null;
  return `•••• ${last4}`;
}
